import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { AdminPanel } from "@/components/site/Admin";
import { MatchForm } from "@/components/site/MatchForm";
import { useSite, ReloginNeeded } from "@/context/SiteContext";
import { errorMessage } from "@/lib/api";
import { money, parseAmount, percent } from "@/lib/format";

function MatchBanner({ s }) {
  const pct = Math.min(percent(s.matched_so_far, s.match_amount), 100);
  const matched = Math.min(s.matched_so_far, s.match_amount);
  const who = s.matcher_name?.trim() || "A generous donor";
  return (
    <div className="of-card overflow-hidden" data-testid="match-banner">
      <div className="on-dark bg-[#24453D] px-6 py-8 text-white sm:px-10">
        <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#F5AE3D]">
          <Sparkles className="h-4 w-4" aria-hidden /> Your gift goes twice as far
        </p>
        <h2 className="mt-3 text-3xl font-semibold text-white sm:text-4xl" data-testid="match-banner-text">
          {who} is matching gifts up to {money(s.match_amount)}
        </h2>
        <p className="mt-2 text-[#D9DEE5]">Every online gift you make now is matched, until the match is reached.</p>
      </div>
      <div className="px-6 py-6 sm:px-10">
        <p className="font-semibold" data-testid="match-progress-text">{money(matched)} matched of {money(s.match_amount)} · {pct}%</p>
        <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-[#E9E2D8]" role="progressbar" aria-valuemin={0} aria-valuemax={100}
          aria-valuenow={pct} aria-label="Matched so far" data-testid="match-progress-bar">
          <div className="h-full rounded-full bg-[#F5AE3D]" style={{ width: `${pct}%`, transition: "width 600ms ease" }} data-testid="match-progress-fill" />
        </div>
      </div>
    </div>
  );
}

function MatchInvite() {
  return (
    <div className="of-card p-6 sm:p-10" data-testid="match-invitation">
      <p className="of-eyebrow">Become the match</p>
      <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">Double the impact.</h2>
      <p className="mt-3 max-w-2xl text-lg text-[#3B4A44]">A matching gift multiplies every donation made during the drive.</p>
      <div className="mt-8"><MatchForm /></div>
    </div>
  );
}

function MatchAdmin({ s }) {
  const { saveSettings } = useSite();
  const [active, setActive] = useState(!!s.match_active);
  const [amount, setAmount] = useState(String(s.match_amount || ""));
  const [name, setName] = useState(s.matcher_name || "");
  const [matched, setMatched] = useState(String(s.matched_so_far ?? 0));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const a = parseAmount(amount);
    const m = matched.trim() === "0" || !matched.trim() ? 0 : parseAmount(matched);
    if (active && !a) return setError("Please enter a match amount of $1 or more.");
    if (m === null) return setError("Please enter matched so far as a number, like 500.");
    setBusy(true);
    setError("");
    try {
      await saveSettings({ match_active: active, match_amount: a || 0, matcher_name: name, matched_so_far: m });
      toast.success(active ? "Saved. The match banner is showing." : "Saved. The invitation is showing again.");
    } catch (err) {
      if (!(err instanceof ReloginNeeded)) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminPanel title="Matching gift" testId="match-admin-panel">
      <label className="flex min-h-[48px] cursor-pointer items-center gap-3 text-base font-semibold">
        <input type="checkbox" className="h-6 w-6 accent-[#24453D]" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="admin-match-active-checkbox" />
        Match is on (show the match banner)
      </label>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor="admin-match-amount" className="of-label">Match amount ($)</label>
          <input id="admin-match-amount" inputMode="decimal" className="of-input" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="admin-match-amount-input" />
        </div>
        <div>
          <label htmlFor="admin-matcher-name" className="of-label">Matcher name (optional)</label>
          <input id="admin-matcher-name" className="of-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-matcher-name-input" />
        </div>
        <div>
          <label htmlFor="admin-matched" className="of-label">Matched so far ($)</label>
          <input id="admin-matched" inputMode="decimal" className="of-input" value={matched} onChange={(e) => setMatched(e.target.value)} data-testid="admin-matched-so-far-input" />
        </div>
      </div>
      {error && <p className="of-error" role="alert" data-testid="match-admin-error">{error}</p>}
      <button type="button" onClick={save} disabled={busy} className="of-btn-soft mt-4 w-full sm:w-auto" data-testid="match-admin-save-button">
        {busy ? "Saving…" : "Save match"}
      </button>
    </AdminPanel>
  );
}

export function MatchingSection({ bare = false }) {
  const { settings: s } = useSite();
  if (!s) return null;
  const key = `${s.match_active}-${s.match_amount}-${s.matched_so_far}-${s.matcher_name}`;
  return (
    <section id="match" aria-label="Matching gift" className={bare ? "" : "of-container py-12 sm:py-16"} data-testid="matching-section">
      {s.match_active ? <MatchBanner s={s} /> : <MatchInvite />}
      <MatchAdmin key={key} s={s} />
    </section>
  );
}
